import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API } from '@/utils/API';

/** One corpus row as returned by `/submissions/:id/answer_values`. */
export interface AnswerValueRow {
  id: string;
  value: string;
  times_used: number;
}

/**
 * Shape of a successful `POST /submissions/:id/answer_values`. Exactly one of
 * the three branches comes back.
 */
export interface AddAnswerValueResult {
  created?: AnswerValueRow;
  /**
   * Near misses: the server found existing spellings close enough that it
   * refused to create a duplicate. Re-post with `force: true` to override.
   */
  did_you_mean?: AnswerValueRow[];
  /**
   * No-disclosure branch: the value was recorded, but the canonical row lives
   * on a submission this user cannot read, so its spelling is deliberately
   * withheld. Confirm using the text the user typed — never invent one.
   */
  accepted?: boolean;
}

export interface AddAnswerValueInput {
  value: string;
  force?: boolean;
}

/**
 * Query key for one question's suggestion list. The trailing `query` segment
 * changes per keystroke (debounced), so mutations invalidate on the
 * `answerValuesScopeKey` prefix to catch every cached search.
 */
export const answerValuesScopeKey = (submissionId: string, slug: string) =>
  ['answer-values', submissionId, slug] as const;

export const answerValuesQueryKey = (
  submissionId: string,
  slug: string,
  query: string
) => [...answerValuesScopeKey(submissionId, slug), query] as const;

const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : null;

const toAnswerValueRow = (value: unknown): AnswerValueRow | null => {
  const record = asRecord(value);
  if (!record) return null;
  if (typeof record.id !== 'string' || typeof record.value !== 'string') {
    return null;
  }
  return {
    id: record.id,
    value: record.value,
    times_used: typeof record.times_used === 'number' ? record.times_used : 0,
  };
};

const toAnswerValueRows = (value: unknown): AnswerValueRow[] =>
  Array.isArray(value)
    ? value
        .map(toAnswerValueRow)
        .filter((row): row is AnswerValueRow => row !== null)
    : [];

/**
 * Best-effort error copy for a non-2xx response. The body may be a Rails
 * `{ errors: ... }` envelope, something else entirely, or unreadable — never
 * let parsing it throw over the top of the real HTTP failure.
 */
const errorMessageFor = async (response: Response): Promise<string> => {
  const fallback = "Couldn't save that value. Please try again.";
  try {
    const body: unknown = await response.json();
    const record = asRecord(body);
    const errors = record?.errors;
    if (typeof errors === 'string' && errors.trim()) return errors;
    if (Array.isArray(errors)) {
      const first = errors.find((entry): entry is string => typeof entry === 'string');
      if (first) return first;
    }
    if (typeof record?.message === 'string' && record.message.trim()) {
      return record.message;
    }
    return fallback;
  } catch {
    return fallback;
  }
};

interface SuggestionsOptions {
  submissionId: string;
  slug: string;
  query: string;
  enabled: boolean;
}

/**
 * Answer Assist (Feature 1) suggestion list. Policy-derived server-side; the
 * frontend never filters, it only renders what it is handed.
 */
export const useAnswerValueSuggestions = ({
  submissionId,
  slug,
  query,
  enabled,
}: SuggestionsOptions) =>
  useQuery<AnswerValueRow[]>({
    queryKey: answerValuesQueryKey(submissionId, slug, query),
    queryFn: async () => {
      const params = new URLSearchParams({ slug });
      if (query) params.set('q', query);
      const response = await API.authenticated.fetch(
        `/submissions/${submissionId}/answer_values?${params.toString()}`
      );
      if (!response.ok) throw new Error('answer_values unavailable');
      const body: unknown = await response.json();
      return toAnswerValueRows(asRecord(body)?.resources);
    },
    enabled,
    staleTime: 15_000,
    retry: false,
  });

/**
 * Adds a value to the corpus. On success the whole suggestion scope is
 * invalidated so the just-added value is present the next time the dropdown
 * opens (the per-keystroke cache entries otherwise stay warm for 15s).
 */
export const useAddAnswerValue = (submissionId: string, slug: string) => {
  const queryClient = useQueryClient();

  return useMutation<AddAnswerValueResult, Error, AddAnswerValueInput>({
    mutationFn: async ({ value, force }) => {
      const response = await API.authenticated.fetch(
        `/submissions/${submissionId}/answer_values`,
        {
          method: 'POST',
          body: JSON.stringify({
            answer_value: { slug, value, ...(force ? { force: true } : {}) },
          }),
        }
      );
      if (!response.ok) throw new Error(await errorMessageFor(response));

      const body: unknown = await response.json();
      const record = asRecord(body);
      const created = toAnswerValueRow(record?.created);
      const didYouMean = toAnswerValueRows(record?.did_you_mean);
      return {
        ...(created ? { created } : {}),
        ...(didYouMean.length ? { did_you_mean: didYouMean } : {}),
        ...(record?.accepted === true ? { accepted: true } : {}),
      };
    },
    onSuccess: async result => {
      if (!result.created && !result.accepted) return;
      await queryClient.invalidateQueries({
        queryKey: answerValuesScopeKey(submissionId, slug),
      });
    },
  });
};
